import React from 'react'
import { Header } from '../header'
import { ThemeProvider } from '../../lib/theme-context'
import { useNavigate, useLocation } from 'react-router-dom'
import { cn } from '../../lib/utils'
import { HomeIcon, UsersIcon, SettingsIcon, SunIcon, MoonIcon } from 'lucide-react'

export const getNoneLayout = (page: React.ReactElement) => page

interface NavItem {
  title: string
  href: string
  icon: React.ReactNode
}

const navItems: NavItem[] = [
  {
    title: 'Dashboard',
    href: '/admin',
    icon: <HomeIcon className="h-5 w-5" />,
  },
]

const managementItems: NavItem[] = [
  {
    title: 'Manage Users',
    href: '/admin/users',
    icon: <UsersIcon className="h-5 w-5" />,
  },
  {
    title: 'Account Settings',
    href: '/admin/account-settings',
    icon: <SettingsIcon className="h-5 w-5" />,
  },
]

export function DefaultLayout({ page }: { page: React.ReactElement }) {
  const navigate = useNavigate()
  const location = useLocation()
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(true)
  const [isDarkMode, setIsDarkMode] = React.useState(false)

  React.useEffect(() => {
    const isDark = localStorage.getItem('darkMode') === 'true'
    setIsDarkMode(isDark)
    if (isDark) {
      document.documentElement.classList.add('dark')
    }
  }, [])

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode)
    document.documentElement.classList.toggle('dark')
    localStorage.setItem('darkMode', (!isDarkMode).toString())
  }

  // Helper function to handle navigation with hash routing
  const navigateTo = (path: string) => {
    console.log(`DefaultLayout: Navigating to ${path}`);
    // Use navigate function from react-router-dom with replace option to ensure proper routing
    navigate(path, { replace: true });
  };

  return (
    <ThemeProvider>
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        {/* Header */}
        <Header sidebarOpen={isSidebarOpen} setSidebarOpen={setIsSidebarOpen} />

        {/* Sidebar */}
        <aside
          className={cn(
            'fixed left-0 top-0 z-40 mt-16 h-screen w-64 border-r border-gray-200 bg-white dark:bg-gray-800 dark:border-gray-700 transition-transform duration-300 ease-in-out lg:translate-x-0',
            !isSidebarOpen && '-translate-x-full',
          )}
        >
          <div className="flex h-full flex-col">
            <div className="flex-1 overflow-y-auto p-4">
              <nav className="space-y-6">
                {/* Main Navigation */}
                <div>
                  <h2 className="px-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Main
                  </h2>
                  <div className="mt-2 space-y-1">
                    {navItems.map((item) => {
                      const isActive = location.pathname === item.href
                      return (
                        <button
                          key={item.href}
                          onClick={() => navigateTo(item.href)}
                          className={cn(
                            'flex w-full items-center rounded-lg px-4 py-3 text-sm font-medium transition-all duration-200',
                            isActive
                              ? 'bg-primary text-white dark:bg-primary-dark'
                              : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50',
                          )}
                        >
                          <span className={cn('mr-3', isActive ? 'text-white' : 'text-gray-400 dark:text-gray-500')}>
                            {item.icon}
                          </span>
                          {item.title}
                        </button>
                      )
                    })}
                  </div>
                </div>

                {/* Management Section */}
                <div>
                  <h2 className="px-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Management
                  </h2>
                  <div className="mt-2 space-y-1">
                    {managementItems.map((item) => {
                      const isActive = location.pathname === item.href
                      
                      // Use the same navigation method for Account Settings as other items
                      if (item.title === 'Account Settings') {
                        return (
                          <button
                            key={item.href}
                            onClick={() => navigateTo(item.href)}
                            className={cn(
                              'flex w-full items-center rounded-lg px-4 py-3 text-sm font-medium transition-all duration-200',
                              isActive
                                ? 'bg-primary text-white dark:bg-primary-dark'
                                : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50',
                            )}
                          >
                            <span className={cn('mr-3', isActive ? 'text-white' : 'text-gray-400 dark:text-gray-500')}>
                              {item.icon}
                            </span>
                            {item.title}
                          </button>
                        );
                      }
                      
                      // Use button for other items
                      return (
                        <button
                          key={item.href}
                          onClick={() => navigateTo(item.href)}
                          className={cn(
                            'flex w-full items-center rounded-lg px-4 py-3 text-sm font-medium transition-all duration-200',
                            isActive
                              ? 'bg-primary text-white dark:bg-primary-dark'
                              : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50',
                          )}
                        >
                          <span className={cn('mr-3', isActive ? 'text-white' : 'text-gray-400 dark:text-gray-500')}>
                            {item.icon}
                          </span>
                          {item.title}
                        </button>
                      )
                    })}
                  </div>
                </div>
              </nav>
            </div>

            {/* Dark Mode Toggle */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={toggleDarkMode}
                className="flex w-full items-center rounded-lg px-4 py-3 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-all duration-200"
              >
                <span className="mr-3 text-gray-400 dark:text-gray-500">
                  {isDarkMode ? <SunIcon className="h-5 w-5" /> : <MoonIcon className="h-5 w-5" />}
                </span>
                {isDarkMode ? 'Light Mode' : 'Dark Mode'}
              </button>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <div className={cn('mt-16 transition-all duration-300 ease-in-out', isSidebarOpen ? 'lg:pl-64' : 'lg:pl-0')}>
          <main className="min-h-[calc(100vh-4rem)] p-4 md:p-6 lg:p-8">
            <div className="mx-auto max-w-screen-2xl">
              {page}
            </div>
          </main>
        </div>
      </div>
    </ThemeProvider>
  )
}

export const getDefaultLayout = (page: React.ReactElement) => <DefaultLayout page={page} />
